import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  MdOutlineBusinessCenter,
  MdOutlinePeople,
  MdOutlineTrendingUp,
} from 'react-icons/md';
import { ReactComponent as LivestockCombine } from '../../../assest/svg/LivestockCombine.svg';
import { populationRecordsApi } from '../../../api/populationRecordsApi';
import { facilitiesApi } from '../../../api/facilitiesApi';
import {
  livestockActorsApi,
  farmerProducersApi,
  cooperativesApi,
} from '../../../api/workforceApi';
import { marketPricesApi } from '../../../api/marketPricesApi';
import { useCollectionSync } from '../../../hooks/useCollectionSync';

// ─── Helpers ──────────────────────────────────────────────────────────────────
async function safeCount(apiFn) {
  try {
    const result = await apiFn();
    const total = Number(result?.pagination?.total ?? result?.pagination?.count ?? result?.pagination?.total_records);
    if (Number.isFinite(total)) return total;
    if (Array.isArray(result?.items)) return result.items.length;
    if (Array.isArray(result?.results)) return result.results.length;
    if (Array.isArray(result)) return result.length;
    return 0;
  } catch {
    return 0;
  }
}

// Keeps unit stable during count-up animation (e.g., always shows "??.??M" for a 42 M target)
function formatAnimated(current, target) {
  if (target === 0) return '0';
  if (target >= 1_000_000_000) return `${(current / 1_000_000_000).toFixed(1)}B`;
  if (target >= 1_000_000)     return `${(current / 1_000_000).toFixed(2)}M`;
  if (target >= 1_000)         return `${(current / 1_000).toFixed(2)}K`;
  return current.toLocaleString();
}

// ─── Icon map ─────────────────────────────────────────────────────────────────
const ICONS = {
  livestock:      LivestockCombine,
  infrastructure: MdOutlineBusinessCenter,
  workforce:      MdOutlinePeople,
  trade:          MdOutlineTrendingUp,
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ stat, index }) => {
  const Icon = ICONS[stat.id];
  const [displayCount, setDisplayCount] = useState(0);
  const domRef   = useRef();
  const frameRef = useRef();
  const startRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Start animation once the card enters the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 },
    );
    if (domRef.current) observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);

  // Re-animate whenever the target value changes (including when API data arrives)
  useEffect(() => {
    if (!visible || stat.loading) return;

    const end = stat.numericValue;
    cancelAnimationFrame(frameRef.current);
    startRef.current = null; // reset so each animation starts fresh

    if (end === 0) { setDisplayCount(0); return; }

    const duration = 1500;
    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      const eased    = progress * (2 - progress); // easeOutQuad
      setDisplayCount(Math.floor(eased * end));
      if (progress < 1) frameRef.current = requestAnimationFrame(animate);
      else              setDisplayCount(end);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [visible, stat.numericValue, stat.loading]);

  return (
    <div
      ref={domRef}
      className="
        bg-gray-50 rounded-xl border border-gray-100 shadow-sm
        px-2 py-5 flex items-start justify-between gap-4
        transition-all duration-300 ease-out group cursor-default
        hover:shadow-md hover:shadow-green-100/40
        hover:border-green-200 hover:-translate-y-0.5
        hover:bg-gradient-to-br hover:from-white hover:to-green-50/20
        active:scale-[0.99]
      "
      style={{ animationDelay: `${200 + index * 100}ms` }}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <p className="text-[11px] font-medium text-gray-900 group-hover:text-gray-600 transition-colors">
          {stat.label}
        </p>

        <p className="text-[20px] font-bold text-gray-900 leading-tight tracking-tight tabular-nums transition-colors group-hover:text-green-700">
          {stat.loading
            ? <span className="inline-block w-20 h-6 bg-gray-200 rounded animate-pulse" />
            : `${stat.prefix ?? ''}${formatAnimated(displayCount, stat.numericValue)}${stat.suffix ? ` ${stat.suffix}` : ''}`}
        </p>

        <p className="text-[8px] text-gray-400 mt-0.5 group-hover:text-gray-500 transition-colors">
          {stat.loading
            ? <span className="inline-block w-28 h-3 bg-gray-100 rounded animate-pulse mt-0.5" />
            : stat.subtext}
        </p>
      </div>

      <div className="
        w-10 h-10 min-w-[30px] rounded-xl flex items-center justify-center
        bg-green-50 text-green-600
        transition-all duration-300
        group-hover:scale-110 group-hover:bg-green-100 group-hover:shadow-md
      ">
        <Icon size={20} />
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const INITIAL_STATS = [
  { id: 'livestock',      label: 'National Livestock Population',      numericValue: 0, prefix: '',  suffix: '',            subtext: 'Across all registered species', loading: true },
  { id: 'infrastructure', label: 'Functional Infrastructure Coverage', numericValue: 0, prefix: '',  suffix: 'Facilities',  subtext: 'Registered facilities nationwide',     loading: true },
  { id: 'workforce',      label: 'Workforce & Producers Registry',     numericValue: 0, prefix: '',  suffix: 'Actors',      subtext: 'Farmers, professionals & cooperatives', loading: true },
  { id: 'trade',          label: 'Trade & Markets Activity',           numericValue: 0, prefix: '₦', suffix: 'Trade Value', subtext: 'Indicative estimate, approved records', loading: true },
];

const OperationalPanel = () => {
  const [stats, setStats] = useState(INITIAL_STATS);

  const reloadStats = useCallback(() => {
    let active = true;

    (async () => {
      const [
        population,
        totalFacilities,
        fullyFunctional,
        actors,
        farmers,
        cooperatives,
        tradeValue,
      ] = await Promise.all([
        populationRecordsApi.totalHeadcount(),
        safeCount(() => facilitiesApi.list({ per_page: 1 })),
        safeCount(() => facilitiesApi.list({ per_page: 1, functional_status: 'fully_functional' })),
        safeCount(() => livestockActorsApi.list({ per_page: 1 })),
        safeCount(() => farmerProducersApi.list({ per_page: 1 })),
        safeCount(() => cooperativesApi.list({ per_page: 1 })),
        marketPricesApi.tradeValueTotal(),
      ]);

      if (!active) return;

      const workforceTotal = actors + farmers + cooperatives;
      const infraSubtext =
        fullyFunctional > 0
          ? `${fullyFunctional.toLocaleString()} Fully Functional`
          : 'Registered facilities nationwide';

      setStats([
        { id: 'livestock',      label: 'National Livestock Population',      numericValue: population,      prefix: '',  suffix: '',            subtext: 'Across all registered species', loading: false },
        { id: 'infrastructure', label: 'Functional Infrastructure Coverage', numericValue: totalFacilities, prefix: '',  suffix: 'Facilities',  subtext: infraSubtext,                             loading: false },
        { id: 'workforce',      label: 'Workforce & Producers Registry',     numericValue: workforceTotal,  prefix: '',  suffix: 'Actors',      subtext: 'Farmers, professionals & cooperatives',   loading: false },
        { id: 'trade',          label: 'Trade & Markets Activity',           numericValue: tradeValue,      prefix: '₦', suffix: 'Trade Value', subtext: 'Indicative estimate, approved records',   loading: false },
      ]);
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const cancel = reloadStats();
    return () => {
      if (typeof cancel === 'function') cancel();
    };
  }, [reloadStats]);

  useCollectionSync([
    populationRecordsApi.collectionKey,
    facilitiesApi.collectionKey,
    livestockActorsApi.collectionKey,
    farmerProducersApi.collectionKey,
    cooperativesApi.collectionKey,
    marketPricesApi.collectionKey,
  ], reloadStats);

  return (
    <section className="animate-fade-in-up">
      <h2 className="text-[15px] font-semibold text-gray-800 mb-4">Operational Panel</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {stats.map((stat, idx) => (
          <StatCard key={stat.id} stat={stat} index={idx} />
        ))}
      </div>
    </section>
  );
};

export default OperationalPanel;
